import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import BankIcon from '../../../assets/svg/account_balance.svg?react';

import { useTranslation } from 'react-i18next';
import AccountNameHolder from '../form-inputs/account-name-holder-input/AccountNameHolderInput';
import SwiftInput from '../form-inputs/swift-input/SwiftInput';
import AccountNumberInput from '../form-inputs/account-number-input/AccountNumberInput';
import BankCountry from '../form-inputs/bank-country-input/BankCountryInput';
import BrushIcon from '../../../assets/svg/brush.svg?react';
import {
  BusinessProfileBankDetails,
  updateBusinessProfileBankDetails,
} from '../../slices/business-profile/businessProfileSlice';
import {
  updateSupplierBankDetails,
  createSupplierBankDetails,
  BankDetails,
} from '../../slices/supplier-bank-details/supplierBankDetails';
import ConfirmModal from '../confirm-modal/ConfirmModal';
import styles from './BankDetailsCard.module.scss';
import CurrencySelector from '../currency-selector/CurrencySelector';
import { useAppDispatch } from '../hooks/useAppDispatch';

import { fetchSupplierById } from '../../slices/suppliers/suppliersSlice';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';

interface BankDetailsCardProps {
  bank?: BankDetails;
  isSupplierBankDetails?: boolean;
  supplierId?: number;
}

export default function BankDetailsCard({
  bank,
  isSupplierBankDetails,
  supplierId,
}: BankDetailsCardProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isEdited, setIsEdited] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const formRef = useRef<HTMLDivElement | null>(null);
  const [popperNode, setPopperNode] = useState<HTMLDivElement | null>(null);

  const toggleEditing = () => {
    setIsEdited((prev) => !prev);
  };

  const [formData, setFormData] = useState<{ supplier?: number } & BankDetails>({
    id: bank?.id,
    company: bank?.company,
    account_number: bank?.account_number ?? '',
    account_name: bank?.account_name ?? '',
    swift_code: bank?.swift_code,
    country: bank?.country,
    currency: bank?.currency || 'AUD',
  });

  const cancelChanges = () => {
    setIsEdited(false);
    if (bank && bank?.id) {
      setFormData({
        id: bank?.id,
        company: bank?.company,
        account_number: bank?.account_number,
        account_name: bank?.account_name,
        swift_code: bank?.swift_code,
        country: bank?.country,
        currency: bank?.currency || 'AUD',
      });
    } else {
      setFormData({
        account_number: '',
        account_name: '',
        swift_code: '',
        country: '',
        currency: 'AUD',
      });
    }
  };

  const saveChanges = () => {
    if (!bank?.id && !isSupplierBankDetails) return;
    if (isSupplierBankDetails) {
      setFormData({ ...formData, supplier: supplierId });
      if (bank?.id) {
        dispatch(showOverlay());
        dispatch(
          updateSupplierBankDetails({
            supplierId: supplierId ?? 0,
            id: bank.id,
            bankDetails: formData,
          })
        )
          .unwrap()
          .then(() => {
            dispatch(fetchSupplierById(supplierId ?? 0))
              .unwrap()
              .finally(() => {
                dispatch(hideOverlay());
              });
          })
          .finally(() => {
            delete formData.supplier;
          });
      } else {
        dispatch(showOverlay());
        dispatch(
          createSupplierBankDetails({
            supplier: supplierId ?? 0,
            bankDetails: formData,
          })
        )
          .unwrap()
          .then(() => {
            dispatch(fetchSupplierById(supplierId ?? 0));
          })
          .finally(() => {
            delete formData.supplier;
            dispatch(hideOverlay());
          });
      }
    } else {
      dispatch(showOverlay());
      dispatch(
        updateBusinessProfileBankDetails({
          companyId: bank?.company ?? 0,
          id: bank?.id ?? 0,
          data: formData,
        })
      )
        .unwrap()
        .finally(() => dispatch(hideOverlay()));
    }
    setIsEdited(false);
    setShowModal(false);
  };
  const confirmExit = () => {
    setIsEdited(false);
    cancelChanges();
    setShowModal(false);
  };

  const handleChange =
    <K extends keyof BusinessProfileBankDetails>(field: K) =>
    (value: BusinessProfileBankDetails[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  useEffect(() => {
    let showModalTimeout: any;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEdited &&
        formRef.current &&
        !formRef.current.contains(event.target as Node) &&
        (!popperNode || !popperNode.contains(event.target as Node)) // Ahora ignora correctamente los clics en el menú
      ) {
        showModalTimeout = setTimeout(() => {
          setShowModal(true);
        }, 0);
      }
    };

    if (isEdited) document.addEventListener('mousedown', handleClickOutside);
    return () => {
      clearTimeout(showModalTimeout);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEdited, popperNode]);

  return (
    <Grid component='div' container className='inputs-editable'>
      <Grid component='div' size={{ xs: 12 }}>
        <Card className={`${styles.card} ${isEdited ? styles.card_border : ''}`} ref={formRef}>
          <CardContent sx={{ paddingTop: 0, paddingBottom: '16px !important' }}>
            <Grid
              component='div'
              container
              style={{
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingLeft: '0 !important',
                paddingRight: '0 !important',
                flexWrap: 'nowrap',
                padding: '16px',
              }}
            >
              <Grid
                component='div'
                container
                style={{ alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}
              >
                <Grid
                  component='div'
                  sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                >
                  <BankIcon />
                </Grid>
                <Grid component='div'>
                  <Typography variant='h6' className='semibold-variant'>
                    {t('businessProfile.bankCardHeader')}
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                component='div'
                size={{ xs: 1 }}
                container
                style={{ justifyContent: 'flex-end' }}
              >
                {!isEdited ? (
                  <Tooltip title={t('buttons.edit')} placement='top'>
                    <IconButton
                      id='bankDetailsCardEditIconBtn'
                      onClick={toggleEditing}
                      sx={{ flexShrink: 0 }}
                    >
                      <BrushIcon id='bankDetailsCardEditIconBtnBrushIcon' />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Button
                      id='bankDetailsCardCancelBtn'
                      type='button'
                      className='btn-default btn-action-basic btn-cancel'
                      role='button'
                      variant='contained'
                      size='large'
                      onClick={cancelChanges}
                    >
                      {t('buttons.cancel')}
                    </Button>
                    <Button
                      id='bankDetailsCardSaveBtn'
                      type='submit'
                      className='btn-default btn-action-basic'
                      role='button'
                      disabled={!isEdited}
                      variant='contained'
                      size='large'
                      onClick={saveChanges}
                    >
                      {t('buttons.save')}
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>

            <Grid component='div' container spacing={2}>
              <Grid component='div' size={{ xs: 12, sm: 6 }}>
                {' '}
                <AccountNameHolder
                  value={formData.account_name}
                  onChange={handleChange('account_name')}
                  disabled={!isEdited}
                />
              </Grid>
              <Grid component='div' size={{ xs: 12, sm: 6 }}>
                {' '}
                <SwiftInput
                  value={formData.swift_code ?? ''}
                  onChange={handleChange('swift_code')}
                  disabled={!isEdited}
                />
              </Grid>
              <Grid component='div' size={{ xs: 12, sm: 6 }}>
                {' '}
                <AccountNumberInput
                  value={formData.account_number}
                  onChange={handleChange('account_number')}
                  disabled={!isEdited}
                />
              </Grid>
              <Grid component='div' size={{ xs: 12, sm: 6 }}>
                {' '}
                <CurrencySelector
                  value={formData.currency ?? ''}
                  onChange={handleChange('currency')}
                  disabled={!isEdited}
                  setPopperRef={setPopperNode}
                />
              </Grid>
              <Grid component='div' size={{ xs: 12, sm: 6 }}>
                {' '}
                <BankCountry
                  value={formData.country ?? ''}
                  onChange={handleChange('country')}
                  disabled={!isEdited}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <ConfirmModal open={showModal} onClose={saveChanges} onConfirm={confirmExit} />
    </Grid>
  );
}
